import { apiRequest, backendAuthEventName } from '../api/client';
import { API_BASE_URL } from '../config/runtime';

const listeners = new Set();
const watched = new Map();
const watchedPosts = new Map();
let postWatchTimer;
const syncPosts = () => sendRealtime('subscribe.posts', { postIds: [...watchedPosts.keys()].slice(-40) });
export const watchPost = id => {
  watchedPosts.set(id, (watchedPosts.get(id) || 0) + 1);
  clearTimeout(postWatchTimer); postWatchTimer = setTimeout(syncPosts, 100);
  return () => {
    const count = (watchedPosts.get(id) || 1) - 1;
    if (count) watchedPosts.set(id, count); else watchedPosts.delete(id);
    clearTimeout(postWatchTimer); postWatchTimer = setTimeout(syncPosts, 100);
  };
};
let owner = null, socket = null, generation = 0, retryTimer, heartbeat, deadline, hasConnected = false;
let attempts = 0, lastPong = 0, connected = false, connecting = false;
const emit = event => {
  // Invalidate shared data before view subscribers read it. This bridge avoids
  // a client/cache/realtime import cycle and makes updates work off-screen too.
  window.dispatchEvent(new CustomEvent('amposter:cache-realtime', { detail: event }));
  listeners.forEach(listener => { try { listener(event); } catch (error) { console.error('Realtime listener failed', error); } });
};
export const isRealtimeConnected = () => connected;
export const subscribeRealtime = listener => { listeners.add(listener); return () => listeners.delete(listener); };
export const sendRealtime = (type, data = {}) => {
  if (!connected || socket?.readyState !== 1) return false;
  socket.send(JSON.stringify({ type, ...data })); return true;
};
const syncWatch = () => {
  const ids = [...watched.keys()];
  sendRealtime(ids.length ? 'subscribe' : 'unsubscribe', ids.length ? { conversationId: ids[ids.length - 1] } : {});
};
export const watchConversation = id => {
  watched.set(id, (watched.get(id) || 0) + 1); syncWatch();
  return () => { const count = (watched.get(id) || 1) - 1; if (count) watched.set(id, count); else watched.delete(id); syncWatch(); };
};
function disconnect() {
  generation++; connected = false; connecting = false;
  clearTimeout(retryTimer); clearTimeout(deadline); clearInterval(heartbeat);
  const previous = socket; socket = null;
  if (previous) { previous.onclose = null; previous.close(); }
}
function retry() {
  if (!owner || navigator.onLine === false) return;
  clearTimeout(retryTimer);
  retryTimer = setTimeout(connect, Math.min(30000, 1000 * 2 ** Math.min(attempts++, 5)) + Math.random() * 500);
}
async function connect() {
  if (!owner || navigator.onLine === false || socket || connecting) return;
  connecting = true;
  const version = ++generation;
  emit({ type: 'connection.status', data: { connected: false } });
  try {
    const { ticket } = await apiRequest('/api/v1/realtime/ticket', { method: 'POST' });
    if (version !== generation || !owner) return;
    const url = new URL(API_BASE_URL || window.location.origin, window.location.origin);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = `${url.pathname.replace(/\/$/, '')}/ws/realtime`; url.search = ''; url.hash = '';
    const ws = new WebSocket(url.toString()); socket = ws; connecting = false;
    deadline = setTimeout(() => ws.close(4000, 'Authentication timed out'), 10000);
    ws.onopen = () => { if (version === generation) ws.send(JSON.stringify({ type: 'authenticate', ticket, active: !document.hidden })); };
    ws.onmessage = message => {
      if (version !== generation) return;
      let event; try { event = JSON.parse(message.data); } catch { return; }
      if (event.type === 'ready') {
        if (hasConnected) emit({ type: 'reconnected' });
        hasConnected = true;
        clearTimeout(deadline); connected = true; lastPong = Date.now(); syncWatch(); syncPosts();
        heartbeat = setInterval(() => { if (Date.now() - lastPong > 60000) ws.close(4001, 'Heartbeat timed out'); else sendRealtime('ping', { active: !document.hidden }); }, 25000);
        emit({ type: 'connection.status', data: { connected: true } });
      }
      if (event.type === 'pong') { lastPong = Date.now(); attempts = 0; }
      if (event.type === 'post.permissions' && event.data?.postId && event.data?.updates) {
        window.dispatchEvent(new CustomEvent('postUpdated', { detail: event.data }));
      }
      emit(event);
    };
    ws.onerror = () => ws.close();
    ws.onclose = event => {
      if (version !== generation) return;
      socket = null; connected = false; clearTimeout(deadline); clearInterval(heartbeat);
      console.warn('Realtime connection closed', { code: event?.code, reason: event?.reason, wasClean: event?.wasClean });
      emit({ type: 'connection.status', data: { connected: false, code: event?.code, reason: event?.reason } }); retry();
    };
  } catch { if (version === generation) { connecting = false; retry(); } }
}
function online() { if (!socket) { clearTimeout(retryTimer); connect(); } }
function offline() { disconnect(); emit({ type: 'connection.status', data: { connected: false } }); }
function visible() {
  if (document.hidden) { sendRealtime('ping', { active: false }); return; }
  if (connected && Date.now() - lastPong > 60000) { disconnect(); connect(); }
  else if (connected) { sendRealtime('ping', { active: !document.hidden }); emit({ type: 'resync', data: { reason: 'visibility' } }); }
  else online();
}
function authChanged() { disconnect(); if (owner) retryTimer = setTimeout(connect, 0); }
export function startRealtime(userId) {
  disconnect(); owner = userId; attempts = 0; hasConnected = false;
  window.addEventListener('online', online); window.addEventListener('offline', offline);
  window.addEventListener(backendAuthEventName, authChanged);
  document.addEventListener('visibilitychange', visible);
  connect();
  return () => {
    owner = null; disconnect();
    window.removeEventListener('online', online); window.removeEventListener('offline', offline);
    window.removeEventListener(backendAuthEventName, authChanged);
    document.removeEventListener('visibilitychange', visible);
  };
}

// Coalesce event bursts; serialize catch-up requests without losing an event
// that arrives while an earlier request is in flight. No periodic API timer.
export function refreshOnEvents(matches, refresh, { initial = true } = {}) {
  let active = true, running = false, dirty = false, timer;
  const run = async () => {
    if (!active || running) return;
    running = true; dirty = false;
    try { await refresh(); } catch { /* Existing view exposes errors; reconnect/manual retry can recover. */ }
    finally { running = false; if (active && dirty) timer = setTimeout(run, 100); }
  };
  const unsubscribe = subscribeRealtime(event => {
    if (!matches(event)) return;
    dirty = true; if (!running) { clearTimeout(timer); timer = setTimeout(run, 100); }
  });
  if (initial) run();
  return () => { active = false; clearTimeout(timer); unsubscribe(); };
}
